import {
  CUSTOM_DETAIL_FIELD_LABELS,
  DetailItemKey,
  HREF_LINKS,
  HTML_TO_TEXT_FIELDS,
  ItemHrefType,
  TRANSPARENCY_LABEL_URL,
} from 'src/app/@shared/detail/detail-config';
import { Component, Input, inject } from '@angular/core';
import {
  FormFieldInputType,
  FormFieldType,
  FormFieldValueType,
} from 'src/app/@shared/form/form.types';
import { TranslateService } from '@ngx-translate/core';
import { stripHtml } from 'src/app/@shared/utils/strip-html';

export type SubItemEntry = {
  value: string;
  label?: string;
  id?: string; // relevant for valueType = "entityArray"
  href?: string;
  suffix?: string; // plain text rendered in parentheses after the value or link
};

export type DetailListSubItem = {
  id: DetailItemKey;
  iconName?: string;
  label: string;
  type: FormFieldType;
  valueType: FormFieldValueType;
  inputType: FormFieldInputType;
  entries: SubItemEntry[];
};

export type DetailListItem = {
  id: string;
  key: DetailItemKey;
  label: string;
  type: FormFieldType;
  valueType: FormFieldValueType;
  inputType: FormFieldInputType;
  subItems: DetailListSubItem[]; // n subItems for `nestedArray` items, 1 subItem for everything else
};

type ItemTextType = ItemHrefType | 'default';
type FormattedDetailValue = {
  usesHtml: boolean;
  value: string;
};
const TRANSPARENCY_LABEL_ICON_SRC = './assets/icons/steering-wheel.svg';

@Component({
  selector: 'detail-list-item',
  standalone: false, // Add imports
  styleUrls: ['./detail-list-item.component.scss'],
  templateUrl: './detail-list-item.component.html',
})
export class DetailListItemComponent {
  @Input() public useIcon: boolean = false;

  public readonly inlineLabelFieldIds: DetailItemKey[] = ['shortName', 'addressCo', 'contactViaNakos'];
  public readonly transparencyLabelUrl = TRANSPARENCY_LABEL_URL;
  public readonly transparencyLabelIconSrc = TRANSPARENCY_LABEL_ICON_SRC;

  public mappedSubItems: {
    id: DetailItemKey;
    iconName?: string;
    value: string;
    label?: string;
    usesHtml: boolean;
  }[] = [];

  private translate = inject(TranslateService);

  private _isUnauthorized: boolean = false;
  private _item: DetailListItem;

  public get isUnauthorized(): boolean {
    return this._isUnauthorized;
  }

  @Input()
  public set isUnauthorized(value: boolean) {
    this._isUnauthorized = value;

    this.updateMappedSubItems();
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public get item(): DetailListItem {
    return this._item;
  }

  @Input({
    required: true,
  })
  public set item(item: DetailListItem) {
    this._item = item;
    this.updateMappedSubItems();
  }

  private updateMappedSubItems(): void {
    if (!this.item) {
      this.mappedSubItems = [];

      return;
    }

    this.mappedSubItems = this.item.subItems.map((subItem) => {
      const textType = this.getTextType(subItem);

      return {
        iconName: subItem.iconName,
        id: subItem.id,
        label: this.translate.instant(CUSTOM_DETAIL_FIELD_LABELS[subItem.id] || subItem.label),
        ...this.getFormattedValue(subItem, textType),
      };
    });
  }

  private getTextType(subItem: DetailListSubItem): ItemTextType {
    if (HREF_LINKS.url.includes(subItem.id)) {
      return 'url';
    }

    if (HREF_LINKS.email.includes(subItem.id)) {
      return 'email';
    }

    return 'default';
  }

  // eslint-disable-next-line max-lines-per-function
  private getFormattedValue(subItem: DetailListSubItem, textType: ItemTextType): FormattedDetailValue {
    if (subItem.valueType === 'entityArray') {
      return this.getEntriesValue(subItem.entries);
    }

    const values = subItem.entries.map((entry) => { return entry.value; });

    if (subItem.id === 'transparencyPdf') {
      return {
        usesHtml: true,
        value: this.getTransparencyPdfHtmlString(values[0]),
      };
    }

    if (HTML_TO_TEXT_FIELDS.includes(subItem.id)) {
      return {
        usesHtml: false,
        value: values.map((value) => { return stripHtml(value); }).join(', '),
      };
    }

    if (subItem.valueType === 'translatable') {
      return {
        usesHtml: false,
        value: subItem.entries.map((entry) => {
          return this.translate.instant(entry.value);
        }).join(', '),
      };
    }

    if (subItem.valueType === 'file') {
      return this.getEntriesValue(subItem.entries.map((entry) => {
        return {
          ...entry,
          href: entry.value,
          value: entry.label || entry.value,
        };
      }));
    }

    if (textType === 'url' || textType === 'email') {
      const prefix = textType === 'email'
        ? 'mailto:'
        : '';

      return this.getEntriesValue(subItem.entries.map((entry) => {
        return {
          ...entry,
          href: `${prefix}${entry.value}`,
        };
      }));
    }

    return this.getEntriesValue(subItem.entries);
  }

  private getEntriesValue(entries: SubItemEntry[]): FormattedDetailValue {
    const usesHtml = entries.some((entry) => { return !!entry.href; });

    return {
      usesHtml,
      value: entries.map((entry) => {
        return usesHtml
          ? this.getEntryHtml(entry)
          : this.getEntryText(entry);
      }).join(', '),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  private getEntryText(entry: SubItemEntry): string {
    return entry.suffix
      ? `${entry.value} (${entry.suffix})`
      : entry.value;
  }

  private getEntryHtml(entry: SubItemEntry): string {
    const value = this.escapeHtml(entry.value);
    const html = entry.href
      ? `<a href="${this.escapeHtml(entry.href)}">${value}</a>`
      : value;

    return entry.suffix
      ? `${html} (${this.escapeHtml(entry.suffix)})`
      : html;
  }

  // eslint-disable-next-line class-methods-use-this
  private escapeHtml(value: string): string {
    return value.replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll('\'', '&#39;');
  }

  private getTransparencyPdfHtmlString(value: string): string {
    return `
      <div class="transparency-item-value">
        <span class="transparency-item-value__inner">
          <img src="./assets/icons/steering-wheel.svg"/>
          ${this.translate.instant('VIEW.ORGANIZATION_DETAIL.TRANSPARENCY_LABEL.PREFIX')}
        </span>
        <a href="${this.transparencyLabelUrl}" target="_blank">
          ${this.translate.instant('VIEW.ORGANIZATION_DETAIL.TRANSPARENCY_LABEL.LABEL')}
        </a>
        <span class="transparency-item-value__inner">
          (<a href="${this.escapeHtml(value)}" target="_blank">
          <img src="./assets/icons/open-outline.svg"/>
          ${this.translate.instant('VIEW.ORGANIZATION_DETAIL.TRANSPARENCY_LABEL.PDF_LINK')}
          </a>)
        </span>
      </div>`;
  }
}
